import type { TitleOfferFragment, TitleOfferLightFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { getJWTVTitleUrl } from '@/helpers/jwtv-helper';
import { openJwTvRentalModal } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { ClickoutUrl, safeOpen } from '@/helpers/clickout-helper';

import { type PlayerModalTitle } from '@/features/title-player/types';
import type { WatchNowOfferFragment } from '@/pages/graphql/fragments/WatchNowOffer.fragment';
import { Domain } from '@/routing/helpers/subdomain-helper';
import { useRouter } from 'vue-router/composables';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { storeToRefs } from 'pinia';
import { transformContextToLinkToContext } from '@/helpers/tracking/tracking-helper';

type VariantPromoInfo = {
	code: string;
	queryParam: string;
};

export function useRentalModal() {
	const router = useRouter();
	const { activeDomain } = storeToRefs(useRoutingStore());
	const { isAccountVerified } = storeToRefs(useUserStore());

	function openRentalModal({
		title,
		offer,
		forceSafeOpen,
		clickoutUrl,
		overrideRedirectUrl,
		expPromoCode,
	}: {
		title: PlayerModalTitle;
		offer: WatchNowOfferFragment | TitleOfferFragment | TitleOfferLightFragment;
		clickoutUrl: ClickoutUrl;

		forceSafeOpen?: boolean;
		overrideRedirectUrl?: boolean;
		expPromoCode?: VariantPromoInfo | null;
	}) {
		const isMainDomain = activeDomain.value === Domain.MAIN;
		const linkContextId = clickoutUrl.contexts?.LinkContext?.link?.self!;
		if (forceSafeOpen || !isMainDomain) {
			const newStandardWebURL = new URL(offer.standardWebURL || '');
			newStandardWebURL.searchParams.set('lcId', linkContextId);
			const newStandardWebURLString = newStandardWebURL.href;
			safeOpen(newStandardWebURLString);
			return;
		}
		if ((isAccountVerified.value && !title?.content?.fullPath) || overrideRedirectUrl) {
			let overrideSuccessUrl: string | null = null;

			if (overrideRedirectUrl) {
				overrideSuccessUrl = getJWTVTitleUrl({
					fullPath: title.content.fullPath,
					linkContextId,
					expPromoCode: expPromoCode?.code,
					fullUrl: true,
				});
			}

			openJwTvRentalModal({
				modalProps: {
					title: title!,
					offer: offer,
					additionalContexts: [
						transformContextToLinkToContext(clickoutUrl.contexts.LinkContext),
						clickoutUrl.contexts.ClickoutContextGraphql,
						clickoutUrl.contexts.TitleContextGraphql,
					],
					overrideSuccessUrl,
					promoCode: expPromoCode?.code,
				},
			});
		} else {
			router.push({
				path: title.content.fullPath,
				query: {
					package: 'jwt',
					...(linkContextId
						? {
								lcId: linkContextId,
								expPC: expPromoCode?.queryParam,
						  }
						: {}),
				},
			});
		}
	}

	return {
		openRentalModal,
	};
}
